import styled from '@emotion/styled';
import { Button, Tooltip, Typography } from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { DateTime } from 'luxon';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { SingleAssignmentFragment } from '../../../../graphql/generated/graphql_offer';
import theme from '../../../../theme';
import { toJSDate } from '../../../../utility/graphql/datetime';
import _OfferDeadlineDisplay from '../../../component/compound/OfferDeadlineDisplay';
import CreativeFormatListItem from '../../../component/compound/OfferDisplay/CreativeFormatListItem';
import _OfferRewardDisplay from '../../../component/compound/OfferRewardDisplay';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import StatusLabel from './StatusLabel';

export interface Props {
  assignment: SingleAssignmentFragment;
  className?: string;
}

const displayStatus = (assignment: SingleAssignmentFragment) => {
  const mid = (() => {
    if ('CANDIDATED' === assignment.status) {
      return `${assignment.status.toLowerCase()}.${assignment.candidatedStatus?.toLowerCase()}`;
    }
    if ('ASSIGNED' === assignment.status) {
      return `${assignment.status.toLowerCase()}.${assignment.assignedStatus?.toLowerCase()}`;
    }
    return assignment.status.toLowerCase();
  })();
  return <FormattedMessage id={`my_assignments.status.${mid}`} />;
};

const AssignedOfferDisplay = ({ assignment, className }: ClassAttributes<HTMLElement> & Props) => {
  const offer = assignment.offer;
  const resizeBaseCreationList = offer.creativeFormats.filter((creativeFormat) => !creativeFormat.resizeBaseId);
  let itemCount = 1;

  const creationList = resizeBaseCreationList.map((creativeFormat, index) => {
    return (
      <CreativeFormatWithResizeItem key={`offer_display.creative_formats.${index}`}>
        <CreativeFormatListItem
          key={`offer_display.creative_format_list.${creativeFormat.id}`}
          creativeFormat={creativeFormat}
          itemIndex={itemCount++}
        />

        {offer.creativeFormats.map((resizeCreativeFormat) => {
          return creativeFormat.id === resizeCreativeFormat.resizeBaseId ? (
            <CreativeFormatListItem
              key={`offer_display.resize_creative_format_list.${resizeCreativeFormat.id}`}
              creativeFormat={resizeCreativeFormat}
              itemIndex={itemCount++}
            />
          ) : null;
        })}
      </CreativeFormatWithResizeItem>
    );
  });

  return (
    <Root className={className}>
      <Content>
        <OfferInfo>
          <OfferReward
            offer={{
              rewardCurrency: offer.rewardCurrency,
              rewardAmount: assignment.rewardAmount,
            }}
            isAvailable={true}
            displayRewardText={offer.matchingFormat == 'competition'}
          />
          <Divider />
          <OfferDeadlineDisplay
            offer={{
              expressDelivery: offer.expressDelivery,
              expectedDue: toJSDate(offer.expectedDue),
            }}
          />
          <Divider />
          <Buttons>
            <Button
              variant="contained"
              to={`/creation_sets/o/${assignment.offer.id}/a/${assignment.id}`}
              component={Link}
            >
              <FormattedMessage id="my_assignments.list.link_label" />
            </Button>
            {assignment.activeStatusUpdated ? (
              <Tooltip title={<FormattedMessage id="my_assignments.list.active_status_updated" />}>
                <NotificationsIcon />
              </Tooltip>
            ) : null}
          </Buttons>
        </OfferInfo>

        <Statuses>
          <StatusLabel status={displayStatus(assignment)} />
          {offer.jobRevisedAt && DateTime.fromISO(offer.jobRevisedAt).plus({ days: 7 }) >= DateTime.local() ? (
            <StatusLabel
              status={
                <FormattedMessage
                  id="projects_list_page.project_list_items.brief_updated_at"
                  values={{ updatedAt: DateTime.fromISO(offer.jobRevisedAt).toFormat('yyyy-MM-dd HH:mm') }}
                />
              }
            />
          ) : null}
        </Statuses>

        <CreationList>{creationList}</CreationList>
      </Content>
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['1fr', 'auto'],
    rows: ['auto'],
    areas: [['content']],
    alignItems: 'center',
  }),
  padding: `${SPACING_REGULAR}px ${SPACING_UNIT * 3}px`,
});

const Content = styled('div')(gridLayoutItem('content'));

const Divider = styled('div')({
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  height: 28,
  marginRight: SPACING_UNIT,
  marginLeft: SPACING_UNIT,
});

const OfferInfo = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'center',
  }),
  marginBottom: SPACING_UNIT,
});

const OfferJobId = styled(Typography)({
  marginBottom: SPACING_UNIT,
});
OfferJobId.defaultProps = { variant: 'caption' };

const OfferReward = styled(_OfferRewardDisplay)({
  ...gridLayoutItem('reward'),
  display: 'inline-block',
  minWidth: 152,
  marginRight: SPACING_UNIT,
});

const OfferDeadlineDisplay = styled(_OfferDeadlineDisplay)({
  ...gridLayoutItem('deadline'),
});

const Buttons = styled('div')({
  ...gridLayoutItem('buttons', { justifySelf: 'flex-end' }),
  marginLeft: SPACING_REGULAR,
});

const CreationList = styled('div')({
  ...gridLayoutItem('creationList'),
});

const CreativeFormatWithResizeItem = styled('div')({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  ':last-child': {
    borderBottomWidth: 0,
  },
});

const Statuses = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'center',
  }),
  marginBottom: SPACING_UNIT,
});

export default AssignedOfferDisplay;
